import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { spRequest } from '../authConfig';
import { getWins, getAllLikeCounts, getDeletedWins, restoreWin, deleteWin } from '../graphService';
import WinDetailModal from './WinDetailModal';
import EditWinModal from './EditWinModal';
import AdminCharts from './AdminCharts';
import { isAdminEmail } from '../adminEmails';
import '../styles/AdminPage.css';

function formatMinutes(total) {
  const mins = Math.round(Number(total) || 0);
  if (mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export default function AdminPage({ siteId, listId }) {
  const { instance, accounts } = useMsal();
  const userEmail = accounts[0]?.username?.toLowerCase() || '';
  const currentUser = accounts[0]?.name || '';

  const [tab, setTab] = useState('liked'); // 'liked' | 'deleted'

  // Liked tab state
  const [wins, setWins] = useState([]);
  const [likeCounts, setLikeCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWin, setSelectedWin] = useState(null);
  const [editingWin, setEditingWin] = useState(null);
  const [sortBy, setSortBy] = useState('likes');
  const [timeRange, setTimeRange] = useState('all'); // 'all' | 'week'
  const [freqFilter, setFreqFilter] = useState(new Set());

  // Deleted tab state
  const [deletedWins, setDeletedWins] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedError, setDeletedError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [selectedDeletedWin, setSelectedDeletedWin] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
      const [winsData, counts] = await Promise.all([
        getWins(tokenRes.accessToken, siteId, listId),
        getAllLikeCounts(tokenRes.accessToken),
      ]);
      setWins(winsData);
      setLikeCounts(counts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, siteId, listId]);

  const fetchDeleted = useCallback(async () => {
    setDeletedLoading(true);
    setDeletedError(null);
    try {
      const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
      const data = await getDeletedWins(tokenRes.accessToken);
      setDeletedWins(data);
    } catch (err) {
      setDeletedError(err.message);
    } finally {
      setDeletedLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (tab === 'deleted') fetchDeleted();
  }, [tab, fetchDeleted]);

  if (!isAdminEmail(userEmail)) {
    return (
      <div className="admin-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  const handleRestore = async (id) => {
    setRestoringId(id);
    try {
      const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
      await restoreWin(tokenRes.accessToken, id);
      setDeletedWins((prev) => prev.filter((w) => w.id !== id));
    } catch {
      // silently ignore
    } finally {
      setRestoringId(null);
    }
  };

  const startOfWeek = new Date();
  startOfWeek.setHours(0, 0, 0, 0);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

  const timeFilteredWins = timeRange === 'week'
    ? wins.filter((w) => w.created && new Date(w.created) >= startOfWeek)
    : wins;

  const sortedWins = [...timeFilteredWins]
    .filter((w) => freqFilter.size === 0 || freqFilter.has(w.frequency))
    .sort((a, b) => {
      if (sortBy === 'likes') {
        const diff = (likeCounts[String(b.id)] || 0) - (likeCounts[String(a.id)] || 0);
        return diff !== 0 ? diff : new Date(b.created) - new Date(a.created);
      }
      if (sortBy === 'oldest') return new Date(a.created) - new Date(b.created);
      return new Date(b.created) - new Date(a.created);
    });

  const totalLikes = Object.values(likeCounts).reduce((s, n) => s + n, 0);

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h2 className="admin-heading">Admin</h2>
          <p className="admin-subheading">Manage and review AI Wins submissions.</p>
        </div>
        {tab === 'liked' && (
          <div className="admin-stats-row">
            <div className="admin-stat-chip">{totalLikes} total ♥</div>
            <div className="admin-stat-chip">{wins.filter(w => likeCounts[String(w.id)] > 0).length} wins liked</div>
          </div>
        )}
        {tab === 'deleted' && (
          <div className="admin-stats-row">
            <div className="admin-stat-chip admin-stat-chip--deleted">{deletedWins.length} deleted</div>
          </div>
        )}
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${tab === 'liked' ? 'active' : ''}`}
          onClick={() => setTab('liked')}
        >
          ♥ Liked Wins
        </button>
        <button
          className={`admin-tab ${tab === 'deleted' ? 'active' : ''}`}
          onClick={() => setTab('deleted')}
        >
          🗑 Deleted Wins
        </button>
      </div>

      {/* Liked Wins Tab */}
      {tab === 'liked' && (
        <>
          {!loading && !error && wins.length > 0 && (
            <AdminCharts
              wins={timeFilteredWins}
              likeCounts={likeCounts}
              onSelectWin={setSelectedWin}
              timeRange={timeRange}
              onTimeRangeChange={(r) => { setTimeRange(r); setFreqFilter(new Set()); }}
              onFreqClick={(freq) => setFreqFilter((prev) => {
                const next = new Set(prev);
                next.has(freq) ? next.delete(freq) : next.add(freq);
                return next;
              })}
              activeFreq={freqFilter}
            />
          )}

          <div className="admin-controls">
            <label className="filter-label">Sort by:</label>
            <select className="dept-filter" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="likes">Most Liked</option>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
            <div className="admin-time-toggle">
              <button
                className={`admin-time-btn ${timeRange === 'all' ? 'active' : ''}`}
                onClick={() => { setTimeRange('all'); setFreqFilter(new Set()); }}
              >
                All Time
              </button>
              <button
                className={`admin-time-btn ${timeRange === 'week' ? 'active' : ''}`}
                onClick={() => { setTimeRange('week'); setFreqFilter(new Set()); }}
              >
                This Week
              </button>
            </div>
            {freqFilter.size > 0 && (
              <button className="admin-freq-clear" onClick={() => setFreqFilter(new Set())}>
                {[...freqFilter].join(', ')} ✕
              </button>
            )}
          </div>

          {loading && <div className="gallery-loading">Loading...</div>}
          {error && <div className="gallery-error">Error: {error}</div>}

          {!loading && !error && (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>♥</th>
                  <th>Task</th>
                  <th>Submitted By</th>
                  <th>Department</th>
                  <th>Frequency</th>
                  <th>Capacity</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {sortedWins.map((win) => {
                  const count = likeCounts[String(win.id)] || 0;
                  return (
                    <tr
                      key={win.id}
                      className={`admin-row ${count > 0 ? 'admin-row--liked' : ''}`}
                      onClick={() => setSelectedWin(win)}
                    >
                      <td className="admin-cell-likes">
                        {count > 0 ? (
                          <span className="admin-like-count">♥ {count}</span>
                        ) : (
                          <span className="admin-like-zero">♡</span>
                        )}
                      </td>
                      <td className="admin-cell-task">{win.task}</td>
                      <td>{win.onBehalfOf || win.submittedBy}</td>
                      <td>{win.department || '—'}</td>
                      <td>{win.frequency || '—'}</td>
                      <td>{formatMinutes(win.timeSavedMinutes)}</td>
                      <td className="admin-cell-date">
                        {new Date(win.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {sortedWins.length > 0 && (
                <tfoot>
                  <tr className="admin-totals-row">
                    <td className="admin-cell-likes">
                      <span className="admin-like-count">
                        ♥ {sortedWins.reduce((s, w) => s + (likeCounts[String(w.id)] || 0), 0)}
                      </span>
                    </td>
                    <td className="admin-cell-task admin-totals-label">
                      {sortedWins.length} win{sortedWins.length !== 1 ? 's' : ''}
                    </td>
                    <td />
                    <td />
                    <td />
                    <td>{formatMinutes(sortedWins.reduce((s, w) => s + (w.timeSavedMinutes || 0), 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </>
      )}

      {/* Deleted Wins Tab */}
      {tab === 'deleted' && (
        <>
          {deletedLoading && <div className="gallery-loading">Loading...</div>}
          {deletedError && <div className="gallery-error">Error: {deletedError}</div>}

          {!deletedLoading && !deletedError && deletedWins.length === 0 && (
            <div className="gallery-empty"><p>No deleted wins.</p></div>
          )}

          {!deletedLoading && !deletedError && deletedWins.length > 0 && (
            <div className="deleted-wins-grid">
              {deletedWins.map((win) => (
                <div key={win.id} className="deleted-win-card" onClick={() => setSelectedDeletedWin(win)}>
                  <div className="deleted-win-card-header">
                    {win.department && <span className="deleted-dept-badge">{win.department}</span>}
                    <span className="deleted-badge">Deleted</span>
                  </div>
                  <p className="deleted-win-task">{win.task}</p>
                  <div className="deleted-win-footer">
                    <div className="deleted-win-meta">
                      <span className="deleted-win-author">{win.onBehalfOf || win.submittedBy}</span>
                      <span className="deleted-win-date">
                        {new Date(win.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <button
                      className="admin-restore-btn"
                      disabled={restoringId === win.id}
                      onClick={(e) => { e.stopPropagation(); handleRestore(win.id); }}
                    >
                      {restoringId === win.id ? 'Restoring...' : '↩ Restore'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {selectedWin && !editingWin && (
        <WinDetailModal
          win={selectedWin}
          onClose={() => setSelectedWin(null)}
          currentUser={currentUser}
          isAdmin
          onEdit={() => setEditingWin(selectedWin)}
          onDelete={async (id) => {
            const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
            await deleteWin(tokenRes.accessToken, id);
            setWins((prev) => prev.filter((w) => w.id !== id));
            setSelectedWin(null);
          }}
        />
      )}

      {editingWin && (
        <EditWinModal
          win={editingWin}
          onClose={() => setEditingWin(null)}
          onSaved={(updated) => {
            setWins((prev) => prev.map((w) => w.id === updated.id ? updated : w));
            setSelectedWin(updated);
            setEditingWin(null);
          }}
        />
      )}

      {selectedDeletedWin && (
        <WinDetailModal
          win={selectedDeletedWin}
          onClose={() => setSelectedDeletedWin(null)}
          currentUser=""
        />
      )}
    </div>
  );
}
